/**
 * FORMATTING VALIDATION UTILITIES
 * 
 * Validates formatting ranges to prevent:
 * - Negative indices
 * - Out-of-bounds ranges
 * - Invalid range ordering (start >= end)
 * - Overlapping ranges (warning only - browser handles it)
 */

import { FormattingRange } from '../types/contentItem';

/**
 * Validation result
 */
export interface FormattingValidation {
  /** True if no errors (warnings are OK) */
  isValid: boolean;
  /** Critical errors that must be fixed */
  errors: string[];
  /** Non-critical warnings (informational) */
  warnings: string[];
}

/**
 * Validate formatting ranges
 * 
 * Checks for:
 * - Negative start indices
 * - End index exceeding text length
 * - Start >= end (invalid range)
 * - Overlapping ranges (warning only)
 * 
 * @param text - The text content being formatted
 * @param formatting - Array of formatting ranges to validate
 * @returns Validation result with errors and warnings
 * 
 * @example
 * ```typescript
 * const validation = validateFormatting('Hello world', [
 *   { start: 0, end: 5, marks: { strong: true } }
 * ]);
 * 
 * if (!validation.isValid) {
 *   console.error('Invalid formatting:', validation.errors);
 * }
 * ```
 */
export function validateFormatting(
  text: string,
  formatting: FormattingRange[]
): FormattingValidation {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  // ✅ FIX: Allow empty strings (valid for empty paragraphs)
  if (typeof text !== 'string') {
    errors.push('Text must be a string');
    return { isValid: false, errors, warnings };
  }
  
  if (!Array.isArray(formatting)) {
    errors.push('Formatting must be an array');
    return { isValid: false, errors, warnings };
  }
  
  const textLength = text.length;
  
  // ✅ FIX: If text is empty but has formatting, that's an error
  if (textLength === 0 && formatting.length > 0) {
    errors.push('Cannot have formatting ranges on empty text');
    return { isValid: false, errors, warnings };
  }
  
  // If text is empty and no formatting, that's valid - return early
  if (textLength === 0) {
    return { isValid: true, errors, warnings };
  }
  
  // Validate each range
  for (let i = 0; i < formatting.length; i++) {
    const range = formatting[i];
    
    // Check range structure
    if (!range || typeof range !== 'object') {
      errors.push(`Range ${i}: must be an object`);
      continue;
    }
    
    if (typeof range.start !== 'number') {
      errors.push(`Range ${i}: start must be a number`);
      continue;
    }
    
    if (typeof range.end !== 'number') {
      errors.push(`Range ${i}: end must be a number`);
      continue;
    }
    
    // Validate indices
    if (range.start < 0) {
      errors.push(`Range ${i}: start index ${range.start} is negative`);
    }
    
    if (range.end > textLength) {
      errors.push(`Range ${i}: end index ${range.end} exceeds text length ${textLength}`);
    }
    
    if (range.start >= range.end) {
      errors.push(`Range ${i}: start ${range.start} >= end ${range.end} (invalid range)`);
    }
    
    // Check for marks
    if (!range.marks || typeof range.marks !== 'object') {
      warnings.push(`Range ${i}: no marks specified (has no effect)`);
    } else if (Object.keys(range.marks).length === 0) {
      warnings.push(`Range ${i}: empty marks object (has no effect)`);
    }
    
    // Check for overlaps with previous ranges
    for (let j = 0; j < i; j++) {
      const prevRange = formatting[j];
      
      // Skip if previous range had errors
      if (typeof prevRange.start !== 'number' || typeof prevRange.end !== 'number') {
        continue;
      }
      
      // Check for overlap: ranges overlap if they're NOT completely separate
      const hasOverlap = !(range.end <= prevRange.start || range.start >= prevRange.end);
      
      if (hasOverlap) {
        warnings.push(
          `Range ${i} [${range.start}, ${range.end}) overlaps with range ${j} [${prevRange.start}, ${prevRange.end}) - ` +
          `browser will handle this, but may not be intended`
        );
      }
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Filter out invalid formatting ranges
 * Returns only ranges that are valid
 * 
 * @param text - The text content
 * @param formatting - Array of formatting ranges
 * @returns Array of valid ranges only
 */
export function filterValidRanges(
  text: string,
  formatting: FormattingRange[]
): FormattingRange[] {
  if (!Array.isArray(formatting)) return [];
  
  const textLength = text.length;
  
  return formatting.filter(range => {
    // Must be object with required fields
    if (!range || typeof range !== 'object') return false;
    if (typeof range.start !== 'number') return false;
    if (typeof range.end !== 'number') return false;
    
    // Must have valid indices
    if (range.start < 0) return false;
    if (range.end > textLength) return false;
    if (range.start >= range.end) return false;
    
    // Must have marks
    if (!range.marks || typeof range.marks !== 'object') return false;
    
    return true;
  });
}

/**
 * Normalize formatting ranges
 * Sorts by start index and merges adjacent ranges with identical marks
 * 
 * @param formatting - Array of formatting ranges
 * @returns Normalized array of ranges
 */
export function normalizeFormatting(formatting: FormattingRange[]): FormattingRange[] {
  if (!Array.isArray(formatting) || formatting.length === 0) return [];
  
  // Sort by start index
  const sorted = [...formatting].sort((a, b) => a.start - b.start);
  
  // Merge adjacent ranges with identical marks
  const merged: FormattingRange[] = [];
  let current = sorted[0];
  
  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i];
    
    // Check if adjacent and have identical marks
    const isAdjacent = current.end === next.start;
    const hasSameMarks = JSON.stringify(current.marks) === JSON.stringify(next.marks);
    
    if (isAdjacent && hasSameMarks) {
      // Merge ranges
      current = {
        start: current.start,
        end: next.end,
        marks: current.marks
      };
    } else {
      // Push current and move to next
      merged.push(current);
      current = next;
    }
  }
  
  // Push last range
  merged.push(current);
  
  return merged;
}

